import { charleyMemoryConfig } from './config';
import { db } from '../../lib/firebase';
import {
  collection,
  addDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
} from 'firebase/firestore';

export class CharleyMemoryApiError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'CharleyMemoryApiError';
    this.code = code;
  }
}

// error code 常數，方便呼叫端判斷
export const CharleyMemoryApiErrorCode = {
  INVALID_PASSWORD: 'invalid_password',
  NETWORK: 'network_error',
  SERVER: 'server_error',
  UNKNOWN: 'unknown_error',
};

const ADMIN_TOKEN_KEY = 'cm_admin_token';
const VIEWER_TOKEN_KEY = 'cm_viewer_token';

function getAdminToken() {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function getViewerToken() {
  try {
    return sessionStorage.getItem(VIEWER_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function setViewerToken(token) {
  try {
    if (token) sessionStorage.setItem(VIEWER_TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

function getAuthHeaders() {
  const adminToken = getAdminToken();
  if (adminToken) return { Authorization: `Bearer ${adminToken}` };
  const viewerToken = getViewerToken();
  if (viewerToken) return { Authorization: `Bearer ${viewerToken}` };
  return {};
}

/**
 * 只做密碼登入（設定 viewer session cookie），不額外讀取 /records。
 * 給 todos 這類只需要「已通過 password gate」的頁面使用。
 */
export async function loginWithPassword({ apiBaseUrl, password }) {
  if (!apiBaseUrl) {
    throw new CharleyMemoryApiError(
      CharleyMemoryApiErrorCode.UNKNOWN,
      'apiBaseUrl is required'
    );
  }

  const base = apiBaseUrl.replace(/\/$/, '');

  try {
    const authRes = await fetch(`${base}/charley-memory/auth/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });

    let authBody = {};
    try {
      authBody = await authRes.json();
    } catch (_) {
      // ignore JSON parse error for auth
    }

    if (!authRes.ok) {
      const msg = typeof authBody.error === 'string' ? authBody.error : 'Authentication failed';
      if (
        authRes.status === 401 ||
        msg.toLowerCase().includes('password')
      ) {
        throw new CharleyMemoryApiError(
          CharleyMemoryApiErrorCode.INVALID_PASSWORD,
          msg
        );
      }
      throw new CharleyMemoryApiError(
        CharleyMemoryApiErrorCode.SERVER,
        msg
      );
    }

    // Backend also returns token=sessionID; keep it as a fallback when cookies are blocked.
    if (typeof authBody?.token === 'string' && authBody.token.trim()) {
      setViewerToken(authBody.token.trim());
    }
  } catch (err) {
    if (err instanceof CharleyMemoryApiError) throw err;
    throw new CharleyMemoryApiError(
      CharleyMemoryApiErrorCode.NETWORK,
      err?.message || 'network error'
    );
  }
}

/**
 * 使用密碼登入並取得所有記錄。
 * 會：
 * 1. POST /charley-memory/auth/password
 * 2. GET  /charley-memory/records（帶上 cookies）
 *
 * 成功時回傳 records 陣列；失敗時丟出 CharleyMemoryApiError。
 */
export async function loginAndFetchRecords({ apiBaseUrl, password }) {
  if (!apiBaseUrl) {
    throw new CharleyMemoryApiError(
      CharleyMemoryApiErrorCode.UNKNOWN,
      'apiBaseUrl is required'
    );
  }

  const base = apiBaseUrl.replace(/\/$/, '');

  try {
    const authRes = await fetch(`${base}/charley-memory/auth/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });

    let authBody = {};
    try {
      authBody = await authRes.json();
    } catch (_) {
      // ignore JSON parse error for auth
    }

    if (!authRes.ok) {
      const msg = typeof authBody.error === 'string' ? authBody.error : 'Authentication failed';
      if (
        authRes.status === 401 ||
        msg.toLowerCase().includes('password')
      ) {
        throw new CharleyMemoryApiError(
          CharleyMemoryApiErrorCode.INVALID_PASSWORD,
          msg
        );
      }
      throw new CharleyMemoryApiError(
        CharleyMemoryApiErrorCode.SERVER,
        msg
      );
    }

    if (typeof authBody?.token === 'string' && authBody.token.trim()) {
      setViewerToken(authBody.token.trim());
    }

    const recordsRes = await fetch(`${base}/charley-memory/records`, {
      headers: { ...getAuthHeaders() },
      credentials: 'include',
    });

    if (!recordsRes.ok) {
      throw new CharleyMemoryApiError(
        CharleyMemoryApiErrorCode.SERVER,
        `failed to load records: ${recordsRes.status}`
      );
    }

    const records = await recordsRes.json();
    return Array.isArray(records) ? records : [];
  } catch (err) {
    if (err instanceof CharleyMemoryApiError) throw err;
    throw new CharleyMemoryApiError(
      CharleyMemoryApiErrorCode.NETWORK,
      err?.message || 'network error'
    );
  }
}

// --------------- Analytics event tracking (Firestore) ---------------

export async function trackEvent(event, data = {}) {
  // Fire-and-forget analytics write to Firestore charley-events collection
  try {
    await addDoc(collection(db, 'charley-events'), {
      event,
      data,
      ts: serverTimestamp(),
    });
  } catch (e) {
    // non-critical, swallow
  }
}

// --------------- Guestbook (Firestore) ---------------

export async function fetchGuestbook() {
  const q = query(collection(db, 'charley-guestbook'), orderBy('created_at', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(entry => !entry.hidden);
}

export async function postGuestbookMessage({ authorName, message, parentId }) {
  const entry = {
    author_name: authorName,
    message,
    parent_id: parentId || null,
    hidden: false,
    created_at: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, 'charley-guestbook'), entry);
  return { id: ref.id, ...entry };
}

export async function toggleGuestbookHidden(id) {
  // Admin-only: fetch current hidden state and toggle it
  const ref = doc(db, 'charley-guestbook', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(`Guestbook entry ${id} not found`);
  const current = snap.data().hidden || false;
  await updateDoc(ref, { hidden: !current });
  return { id, hidden: !current };
}

export async function deleteGuestbookEntry(id) {
  await deleteDoc(doc(db, 'charley-guestbook', id));
}

// --------------- Todos ---------------
// type: 'shared' = 情侶共用, 'dev' = 開發待辦。不傳 type 則回傳全部（給 admin 用）

export async function fetchTodos(type) {
  const base = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('API base URL not configured');
  const url = type ? `${base}/charley-memory/todos?type=${encodeURIComponent(type)}` : `${base}/charley-memory/todos`;
  const res = await fetch(url, { headers: { ...getAuthHeaders() }, credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function createTodo({ text, createdBy, type: todoType, category }) {
  const base = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('API base URL not configured');
  const body = { text, created_by: createdBy };
  if (todoType) body.type = todoType;
  if (typeof category === 'string') body.category = category;
  const res = await fetch(`${base}/charley-memory/todos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function fetchDevTodos() {
  return fetchTodos('dev');
}

export async function createDevTodo({ text, category }) {
  return createTodo({ text, createdBy: '', type: 'dev', category });
}

export async function toggleTodoDone(id) {
  const base = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('API base URL not configured');
  const res = await fetch(`${base}/charley-memory/todos/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ toggle_done: true }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function updateTodo(id, updates = {}) {
  const base = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('API base URL not configured');
  const body = {};
  if (typeof updates.text === 'string') body.text = updates.text;
  if (typeof updates.type === 'string') body.type = updates.type;
  if (typeof updates.category === 'string') body.category = updates.category;
  const res = await fetch(`${base}/charley-memory/todos/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error || `HTTP ${res.status}`);
  }
  return res.json().catch(() => ({}));
}

export async function reorderTodos({ type, ids }) {
  const base = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('API base URL not configured');
  const res = await fetch(`${base}/charley-memory/todos/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: JSON.stringify({ type, ids }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json().catch(() => ({}));
}

export async function deleteTodo(id) {
  const base = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('API base URL not configured');
  const res = await fetch(`${base}/charley-memory/todos/${id}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders() },
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
}

// --------------- Admin Events (analytics) ---------------
/**
 * @param {{ from?: string; to?: string; event_type?: string; session_id?: string }} opts - from/to RFC3339
 */
export async function fetchEvents(opts = {}) {
  const base = (charleyMemoryConfig.apiBaseUrl || '').replace(/\/$/, '');
  if (!base) throw new Error('API base URL not configured');
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  if (opts.event_type) params.set('event_type', opts.event_type);
  if (opts.session_id) params.set('session_id', opts.session_id);
  const qs = params.toString();
  const url = qs ? `${base}/charley-memory/admin/events?${qs}` : `${base}/charley-memory/admin/events`;
  const res = await fetch(url, { headers: { ...getAuthHeaders() }, credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

